import { useState, useEffect, useCallback } from 'react';
import { pushNotificationService, NotificationPermissionResult } from '../services/notifications/push-notification.service';

export interface UsePushNotificationsReturn {
  // Permission state
  permission: NotificationPermission;
  isSupported: boolean;
  isGranted: boolean;
  
  // Actions
  requestPermission: () => Promise<NotificationPermissionResult>;
  
  // Notification methods
  showMentionNotification: (data: {
    mentionedBy: string;
    clubName: string;
    channelName: string;
    messagePreview: string;
    clubId: string;
    channelId: string;
  }) => Promise<boolean>;
  
  showRoundShareNotification: (data: {
    playerName: string;
    clubName: string;
    courseName: string;
    score: number;
    parDifferential: number;
    clubId: string;
  }) => Promise<boolean>;
  
  showBadgeNotification: (data: {
    badgeName: string;
    badgeIcon: string;
    points: number;
    description: string;
  }) => Promise<boolean>;
  
  // State
  isRequestingPermission: boolean;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  
  const isSupported = pushNotificationService.isSupported();
  const isGranted = permission === 'granted';

  // Update permission state on mount and when it changes
  useEffect(() => {
    if (!isSupported) return;
    
    const updatePermission = () => {
      setPermission(pushNotificationService.getPermissionStatus());
    };
    
    updatePermission();
    
    // Listen for permission changes (not all browsers support this)
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then(permissionStatus => {
        permissionStatus.onchange = updatePermission;
      }).catch(() => {
        // Permission API not supported, that's okay
      });
    }
  }, [isSupported]);

  const requestPermission = useCallback(async (): Promise<NotificationPermissionResult> => {
    if (!isSupported) {
      return {
        granted: false,
        permission: 'denied',
        reason: 'Browser does not support notifications'
      };
    }
    
    setIsRequestingPermission(true);
    
    try {
      const result = await pushNotificationService.requestPermission();
      setPermission(result.permission);
      return result;
    } finally {
      setIsRequestingPermission(false);
    }
  }, [isSupported]);

  const showMentionNotification = useCallback(async (data: {
    mentionedBy: string;
    clubName: string;
    channelName: string;
    messagePreview: string;
    clubId: string;
    channelId: string;
  }): Promise<boolean> => {
    if (!isGranted) return false;
    
    const shouldShow = await pushNotificationService.shouldShowNotification('mentions');
    if (!shouldShow) return false;
    
    return pushNotificationService.showMentionNotification(data);
  }, [isGranted]);

  const showRoundShareNotification = useCallback(async (data: {
    playerName: string;
    clubName: string;
    courseName: string;
    score: number;
    parDifferential: number;
    clubId: string;
  }): Promise<boolean> => {
    if (!isGranted) return false;
    
    const shouldShow = await pushNotificationService.shouldShowNotification('roundShares');
    if (!shouldShow) return false;
    
    return pushNotificationService.showRoundShareNotification(data);
  }, [isGranted]);

  const showBadgeNotification = useCallback(async (data: {
    badgeName: string;
    badgeIcon: string;
    points: number;
    description: string;
  }): Promise<boolean> => {
    if (!isGranted) return false;
    
    const shouldShow = await pushNotificationService.shouldShowNotification('badges');
    if (!shouldShow) return false;
    
    return pushNotificationService.showBadgeNotification(data);
  }, [isGranted]);

  return {
    // Permission state
    permission,
    isSupported,
    isGranted,
    
    // Actions
    requestPermission,
    
    // Notification methods
    showMentionNotification,
    showRoundShareNotification,
    showBadgeNotification,
    
    // State
    isRequestingPermission
  };
}